import { ArrowLeft, Calendar, User, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/hooks/useLanguage';
import { translations } from '@/lib/translations';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import EventRegistrationForm from '@/components/EventRegistrationForm';

interface BlogArticleProps {
  articleId: string;
  onBack: () => void;
}

interface ArticleData {
  id: string;
  slug: string;
  title: string;
  content: string;
  excerpt: string;
  date: string;
  image: string;
  category: string;
  author?: string;
  readTime?: number;
  tags?: string[];
  meta?: {
    description: string;
    keywords: string;
  };
}

export function BlogArticle({ articleId }: BlogArticleProps) {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [article, setArticle] = useState<ArticleData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const goToBlogList = () => {
    const blogPath = language === 'it' ? '/blog' : `/${language}/blog`;
    navigate(blogPath);
  };

  useEffect(() => {
    const loadArticle = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Carica l'articolo dal file JSON specifico per lingua
        const response = await fetch(`/content/blog/articles/${articleId}-${language}.json`);
        
        if (!response.ok) {
          throw new Error(`Articolo non trovato: ${articleId}-${language}`);
        }
        
        const articleData = await response.json();
        setArticle(articleData);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Errore nel caricamento dell\'articolo');
      } finally {
        setLoading(false);
      }
    };

    loadArticle();
  }, [articleId, language]);

  // SEO Meta Tags Management
  useEffect(() => {
    if (article && article.title && article.excerpt) {
      const pageTitle = `${article.title} | Residencia Panama`;
      const metaDescription = article.meta?.description || article.excerpt;
        const metaKeywords = article.meta?.keywords || '';
      
      document.title = pageTitle;
      
      // Update or create meta description
      let descriptionMeta = document.querySelector('meta[name="description"]') as HTMLMetaElement;
      if (descriptionMeta) {
        descriptionMeta.setAttribute('content', metaDescription);
      } else {
        descriptionMeta = document.createElement('meta');
        (descriptionMeta as HTMLMetaElement).name = 'description';
        (descriptionMeta as HTMLMetaElement).content = metaDescription;
        document.head.appendChild(descriptionMeta);
      }

      // Update or create meta keywords
      let keywordsMeta = document.querySelector('meta[name="keywords"]') as HTMLMetaElement;
      if (keywordsMeta) {
        keywordsMeta.setAttribute('content', metaKeywords);
      } else if (metaKeywords) {
        keywordsMeta = document.createElement('meta');
        (keywordsMeta as HTMLMetaElement).name = 'keywords';
        (keywordsMeta as HTMLMetaElement).content = metaKeywords;
        document.head.appendChild(keywordsMeta);
      }

      // Structured data is rendered via JSX below to avoid DOM mismatches
    }

    return () => {
      // No manual DOM cleanup for structured data to prevent React unmount errors under StrictMode
    };
  }, [article, language]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(language === 'it' ? 'it-IT' : language === 'es' ? 'es-ES' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // SEO structured data - solo quando l'articolo è disponibile
  const structuredData = article ? {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": article.title,
    "description": article.excerpt,
    "image": article.image,
    "datePublished": article.date,
    "dateModified": article.date,
    "author": {
      "@type": "Organization",
      "name": "RS International Services"
    },
    "publisher": {
      "@type": "Organization",
      "name": "RS International Services",
      "logo": {
        "@type": "ImageObject",
        "url": "/rs-logo.svg"
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": window.location.href
    }
  } : null;

  // Stati di caricamento ed errore
  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Caricamento articolo...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Errore</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <Button onClick={goToBlogList} variant="outline">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Torna al blog
          </Button>
        </div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="text-gray-400 text-6xl mb-4">📄</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Articolo non trovato</h2>
          <p className="text-gray-600 mb-4">L'articolo richiesto non esiste.</p>
          <Button onClick={goToBlogList} variant="outline">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Torna al blog
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* SEO Meta Tags */}
      {article && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
        />
      )}
      
      {/* Hero Header */}
      {article && (
        <div className="relative bg-gradient-to-br from-blue-900 via-blue-800 to-blue-700 text-white">
          <div className="absolute inset-0 bg-black/20"></div>
          <div className="relative container mx-auto px-4 py-8">
            <div className="max-w-4xl mx-auto">
              {/* Back Button */}
              <Button
                variant="ghost"
                onClick={goToBlogList}
                className="mb-6 text-white hover:text-blue-200 hover:bg-white/10 backdrop-blur-sm"
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                {translations[language].blog.backToBlog}
              </Button>
              
              {/* Breadcrumb */}
              <nav className="text-sm text-blue-200 mb-6">
                <span className="hover:text-white transition-colors">Home</span>
                <span className="mx-2">/</span>
                <span className="hover:text-white transition-colors">Blog</span>
                <span className="mx-2">/</span>
                {article.title && (
          <span className="text-white font-medium">{article.title}</span>
        )}
              </nav>
              
              {/* Category Badge */}
              {article.category && (
                <div className="mb-6">
                  <span className="inline-block px-4 py-2 text-sm font-semibold text-blue-900 bg-white/90 backdrop-blur-sm rounded-full border border-white/20">
                    {article.category}
                  </span>
                </div>
              )}
              
              {/* Article Title */}
              {article.title && (
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                  {article.title}
                </h1>
              )}
              
              {/* Meta Information */}
              <div className="flex flex-wrap items-center gap-6 text-blue-100 mb-8">
                {article.date && (
                  <div className="flex items-center">
                    <Calendar className="w-5 h-5 mr-3" />
                    <span className="font-medium">{formatDate(article.date)}</span>
                  </div>
                )}
                
                {article.author && (
                  <div className="flex items-center">
                    <User className="w-5 h-5 mr-3" />
                    <span className="font-medium">{article.author}</span>
                  </div>
                )}
                
                {article.readTime && (
                  <div className="flex items-center">
                    <Clock className="w-5 h-5 mr-3" />
                    <span className="font-medium">{article.readTime} min di lettura</span>
                  </div>
                )}
              </div>
              
              {/* Excerpt */}
              {article.excerpt && (
                <p className="text-xl text-blue-100 leading-relaxed max-w-3xl mb-8">
                  {article.excerpt}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Article Content */}
      {article && article.content && (
        <article className="container mx-auto px-4 py-12 max-w-4xl">

          {/* Visible Back to Blog button on white background */}
          <div className="mb-6">
            <Button 
              variant="outline" 
              onClick={goToBlogList}
              className="flex items-center"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              {translations[language].blog.backToBlog}
            </Button>
          </div>

          {/* Article Body Container */}
          <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">

            {/* Article Body */}
            <div className="prose prose-lg prose-blue max-w-none">
              <div 
                className="text-gray-800 leading-relaxed space-y-6"
                style={{
                  fontSize: '1.125rem',
                  lineHeight: '1.75',
                }}
              >
{/* Render content - now all languages use markdown parsing */}
                  {article.content.split('\n\n').map((paragraph, index) => {
                    // Handle headings
                    if (paragraph.startsWith('# ')) {
                      return (
                        <h2 key={index} className="text-3xl font-bold text-gray-900 mt-12 mb-6 border-b-2 border-blue-600 pb-2">
                          {paragraph.replace('# ', '')}
                        </h2>
                      );
                    }
                    if (paragraph.startsWith('## ')) {
                      return (
                        <h3 key={index} className="text-2xl font-semibold text-gray-900 mt-10 mb-4">
                          {paragraph.replace('## ', '')}
                        </h3>
                      );
                    }
                    if (paragraph.startsWith('### ')) {
                      return (
                        <h4 key={index} className="text-xl font-semibold text-gray-900 mt-8 mb-3">
                          {paragraph.replace('### ', '')}
                        </h4>
                      );
                    }
                    
                    // Handle tables
                    if (paragraph.includes('|') && paragraph.includes('---')) {
                      const lines = paragraph.split('\n').filter(line => line.trim());
                      const headerLine = lines[0];
                      const separatorLine = lines[1];
                      const dataLines = lines.slice(2);
                      
                      if (headerLine && separatorLine && separatorLine.includes('---')) {
                        const headers = headerLine.split('|').map(h => h.trim()).filter(h => h);
                        const rows = dataLines.map(line => 
                          line.split('|').map(cell => cell.trim()).filter(cell => cell)
                        );
                        
                        return (
                          <div key={index} className="my-8 overflow-x-auto">
                            <table className="w-full border-collapse border border-gray-300 bg-white shadow-sm rounded-lg">
                              <thead>
                                <tr className="bg-blue-50">
                                  {headers.map((header, headerIndex) => (
                                    <th key={headerIndex} className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-900">
                                      {header}
                                    </th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {rows.map((row, rowIndex) => (
                                  <tr key={rowIndex} className={rowIndex % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                                    {row.map((cell, cellIndex) => (
                                      <td key={cellIndex} className="border border-gray-300 px-4 py-3 text-gray-800">
                                        {cell}
                                      </td>
                                    ))}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        );
                      }
                    }

                    // Handle lists
                    if (paragraph.includes('* ') || paragraph.includes('1. ')) {
                      const items = paragraph.split('\n').filter(line => line.trim());
                      const isOrdered = items[0].match(/^\d+\./);
                      
                      return (
                        <div key={index} className="my-6">
                          {isOrdered ? (
                            <ol className="list-decimal list-inside space-y-2 ml-4">
                              {items.map((item, itemIndex) => (
                                <li key={itemIndex} className="text-gray-800 leading-relaxed">
                                  {item.replace(/^\d+\.\s*/, '')}
                                </li>
                              ))}
                            </ol>
                          ) : (
                            <ul className="list-disc list-inside space-y-2 ml-4">
                              {items.map((item, itemIndex) => (
                                <li key={itemIndex} className="text-gray-800 leading-relaxed">
                                  {item.replace(/^\*\s*/, '')}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      );
                    }
                    
                    // Handle bold text in markdown
                    const processMarkdownText = (text: string) => {
                      // Replace **text** with <strong>text</strong>
                      return text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
                    };
                    
                    // Handle regular paragraphs
                    return (
                      <p key={index} className="text-gray-800 leading-relaxed mb-6" 
                         dangerouslySetInnerHTML={{ __html: processMarkdownText(paragraph) }}>
                      </p>
                    );
                  })}
              </div>
            </div>

            {/* Event Registration Form (conditional) */}
            {articleId === 'conferenza-panama-la-habana' && (
              <EventRegistrationForm />
            )}

            {/* Article Footer */}
            <footer className="mt-16 pt-12 border-t border-gray-200">
              <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl p-8 md:p-12 text-center text-white">
                <div className="max-w-2xl mx-auto">
                  <h3 className="text-2xl md:text-3xl font-bold mb-4">
                    Pronto per la tua residenza a Panama?
                  </h3>
                  <p className="text-blue-100 text-lg mb-6">
                    I nostri esperti ti guideranno passo dopo passo nell'intero processo. 
                    Ottieni una consulenza personalizzata e inizia il tuo percorso verso Panama.
                  </p>
                  <div className="flex flex-col items-center gap-4 justify-center">
                    <Button 
                      onClick={() => window.location.href = 'mailto:info@residenciapanama.com'}
                      size="lg"
                      className="bg-white text-blue-700 hover:bg-blue-50 px-8 py-3 font-semibold"
                    >
                      Contattaci Ora
                    </Button>
                    <p className="text-blue-100 text-sm">
                      info@residenciapanama.com
                    </p>
                  </div>
                </div>
              </div>
            </footer>
          </div>
        </article>
      )}
    </div>
  );
}